import React, { useEffect, useState, useMemo } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import { TrendingUp, RefreshCw, Trophy, Search, ArrowDownToLine, Lock, History } from "lucide-react";
import { TimeFilter, periodRange } from "../components/TimeFilter";
import api from "../lib/api";

export default function Wallets() {
  const [physios, setPhysios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/physios?limit=500");
        if (!alive) return;
        setPhysios(data);
        if (data[0]) setSelectedId(data[0].id);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  // Point 39 — search the WHOLE physio master (server-side), not just the loaded rows.
  useEffect(() => {
    const term = query.trim();
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const qs = term ? `search=${encodeURIComponent(term)}&limit=1000` : "limit=500";
        const { data } = await api.get(`/physios?${qs}`);
        if (alive) setPhysios(data);
      } catch { /* swallow */ }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  async function refresh() {
    if (!selectedId) return;
    const { data } = await api.get(`/physios/${selectedId}`);
    setDetail(data);
  }
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/physios/${selectedId}`);
        if (alive) setDetail(data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  const filteredPhysios = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return physios;
    return physios.filter((p) => {
      const fields = [
        p.first_name, p.last_name, p.physio_code, p.mobile,
        p.city, p.state, p.erp_outlet_id, `${p.first_name || ""} ${p.last_name || ""}`,
      ].map((s) => (s || "").toString().toLowerCase());
      return fields.some((f) => f.includes(q));
    });
  }, [physios, query]);

  const w = detail?.wallet || {};
  const txns = detail?.transactions || [];
  const totals = (w.pending || 0) + (w.active || 0) + (w.reserved || 0);

  // ---- Point 4: period filter on the Physio Report ledger ----
  const { start: pStart, end: pEnd } = periodRange(preset, customStart, customEnd);
  const periodActive = !!(pStart && pEnd);
  const txnDate = (t) => (t.order_date || t.at || "").toString().slice(0, 10);
  const periodTxns = periodActive ? txns.filter((t) => { const d = txnDate(t); return d && d >= pStart && d <= pEnd; }) : txns;
  const periodEarned = periodTxns.reduce((s, t) => s + (t.status === "active" ? (t.active_points ?? t.points ?? 0) : 0), 0);
  const periodPending = periodTxns.reduce((s, t) => s + (t.status === "pending" ? (t.pending_points ?? t.points ?? 0) : 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Loyalty Engine"
        title="Physio Report"
        subtitle="Per-physio wallet and milestone view — opening balance (carry-forward from April 2026), pending order points, reserved (blocked) reward redemptions, active spendable points, and the full ledger. Driven by Field Assist order + delivery webhooks."
        testId="wallets-page"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card title="Pick a physio" className="lg:col-span-1">
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, ERP ID, mobile, city…"
              className="w-full pl-8 pr-3 py-1.5 text-[12.5px] bg-slate-50 border border-slate-200 rounded"
              data-testid="physio-report-search"
            />
          </div>
          <div className="text-[11px] text-slate-400 mb-1">{filteredPhysios.length} of {physios.length}</div>
          <div className="max-h-[420px] overflow-y-auto space-y-1">
            {filteredPhysios.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors text-[13px] ${selectedId === p.id ? "border-teal-700 bg-teal-50 text-teal-900" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid={`wallet-physio-${p.id}`}
              >
                <div className="font-medium">{p.first_name} {p.last_name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{p.erp_outlet_id || p.mobile}</div>
              </button>
            ))}
            {filteredPhysios.length === 0 && (
              <div className="text-center text-slate-400 text-[12px] py-6">No physios match this search.</div>
            )}
          </div>
        </Card>

        <div className="lg:col-span-3 space-y-4">
          {detail && (
            <>
              <TimeFilter preset={preset} setPreset={setPreset} customStart={customStart} setCustomStart={setCustomStart} customEnd={customEnd} setCustomEnd={setCustomEnd} testId="wallets-time-filter" />
              {periodActive && (
                <div className="grid grid-cols-2 gap-3" data-testid="wallet-period-summary">
                  <div className="surface p-3">
                    <div className="small-caps text-teal-700">Active points · period</div>
                    <div className="serif-display text-2xl numerals mt-1" data-testid="wallet-period-earned">{fmt(periodEarned)}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{pStart} → {pEnd}</div>
                  </div>
                  <div className="surface p-3">
                    <div className="small-caps text-amber-700">Pending points · period</div>
                    <div className="serif-display text-2xl numerals mt-1" data-testid="wallet-period-pending">{fmt(periodPending)}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{periodTxns.length} transaction(s)</div>
                  </div>
                </div>
              )}
              {/* Transaction wallet */}
              <Card title={`${detail.physio.first_name} ${detail.physio.last_name} · Transaction Wallet`}
                subtitle={`ERP ${detail.physio.erp_outlet_id || "—"} · ${detail.physio.city || "—"}, ${detail.physio.state || "—"}`}
                action={<button onClick={refresh} className="btn-secondary !py-1 flex items-center gap-1.5"><RefreshCw size={12} /> Refresh</button>}>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {[
                    { k: "Opening", v: w.opening_balance, tone: "slate", testId: "wallet-opening", icon: ArrowDownToLine, hint: "Carry-forward from Apr 2026" },
                    { k: "Pending", v: w.pending, tone: "amber", testId: "wallet-pending", hint: "Awaiting delivery validation" },
                    { k: "Active", v: w.active, tone: "emerald", testId: "wallet-active", hint: "Spendable now" },
                    { k: "Reserved", v: w.reserved, tone: "rose", testId: "wallet-reserved", icon: Lock, hint: "Blocked for reward redemption" },
                    { k: "Redeemed", v: w.redeemed, tone: "slate", testId: "wallet-redeemed" },
                    { k: "Expired", v: w.expired, tone: "slate", testId: "wallet-expired" },
                  ].map((c) => {
                    const Icon = c.icon;
                    return (
                      <div key={c.k} className="surface p-3" data-testid={c.testId}>
                        <div className="small-caps text-slate-500 flex items-center gap-1">{Icon && <Icon size={11} />}{c.k}</div>
                        <div className="serif-display text-2xl numerals mt-1">{fmt(c.v)}</div>
                        <div className="h-1 mt-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${c.tone === "amber" ? "bg-amber-500" : c.tone === "emerald" ? "bg-teal-700" : c.tone === "rose" ? "bg-rose-600" : "bg-slate-400"}`} style={{ width: `${Math.min(100, (c.v || 0) / Math.max(1, totals) * 100)}%` }} />
                        </div>
                        {c.hint && <div className="text-[10.5px] text-slate-500 mt-1.5">{c.hint}</div>}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Milestone wallets */}
              <Card title="Milestone wallets" subtitle="Qtr 1 Program resets each quarter · Annual Program accumulates April → March">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { k: "Qtr 1 Program", v: w.quarterly_points, target: 50000, hint: "Resets every quarter · optional carry-forward", icon: TrendingUp },
                    { k: "Annual Program", v: w.annual_points, target: 500000, hint: "FY April → March accumulation", icon: Trophy },
                  ].map((m) => (
                    <div key={m.k} className="surface p-4">
                      <div className="flex items-center justify-between">
                        <div className="small-caps text-teal-700 flex items-center gap-1.5"><m.icon size={12} /> {m.k}</div>
                        <Tag tone="teal">{Math.round((m.v || 0) / m.target * 100)}%</Tag>
                      </div>
                      <div className="serif-display text-3xl numerals mt-2">{fmt(m.v)}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">target {fmt(m.target)} pts</div>
                      <div className="h-1.5 mt-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-700" style={{ width: `${Math.min(100, (m.v || 0) / m.target * 100)}%` }} />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">{m.hint}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Transactions ledger */}
              <Card title="Transactions ledger" subtitle={periodActive ? `${periodTxns.length} entries in ${pStart} → ${pEnd}` : `Last ${txns.length} entries — incl. opening balance and carry-forward from April 2026`} action={<History size={14} className="text-slate-400" />}>
                {periodTxns.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-[12px]">{periodActive ? "No transactions in this period." : "No transactions yet."}</div>
                ) : (
                  <div className="overflow-x-auto" data-testid="txn-ledger">
                    <table className="w-full text-[12px]">
                      <thead className="text-slate-500 small-caps">
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-2">When</th>
                          <th className="text-left py-2">Type</th>
                          <th className="text-left py-2">Source / Note</th>
                          <th className="text-right py-2">Amount (₹)</th>
                          <th className="text-right py-2">Points</th>
                          <th className="text-left py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodTxns.map((t) => (
                          <tr key={t.id} className="border-b border-slate-50" data-testid={`txn-${t.id}`}>
                            <td className="py-2 numerals">{t.at ? new Date(t.at).toLocaleString() : "—"}</td>
                            <td className="py-2"><Tag tone={txnTone(t.type)}>{prettyType(t.type)}</Tag></td>
                            <td className="py-2 text-slate-600 max-w-md truncate">{t.note || t.order_ref || t.source || "—"}</td>
                            <td className="py-2 text-right numerals">{t.amount != null ? fmt(t.amount) : "—"}</td>
                            <td className="py-2 text-right numerals font-medium">{(t.points || 0) > 0 ? `+${fmt(t.points)}` : fmt(t.points)}</td>
                            <td className="py-2"><Tag tone={statusTone(t.status)}>{t.status || "—"}</Tag></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function prettyType(t) {
  return ({
    opening_balance: "Opening / Carry-forward",
    carry_forward: "Carry-forward",
    pending: "Order (pending)",
    order: "Order",
    sale: "Sale",
    delivery: "Delivery",
    welcome_bonus: "Welcome bonus",
    referral_bonus: "Referral bonus",
    redeem: "Redeem",
    redeem_reserve: "Reserve (redemption)",
    reverse: "Reverse",
    expire: "Expire",
    milestone_unlock: "Milestone unlock",
  })[t] || t || "—";
}
function txnTone(t) {
  if (["pending", "order", "sale"].includes(t)) return "amber";
  if (["welcome_bonus", "referral_bonus", "milestone_unlock"].includes(t)) return "emerald";
  if (["redeem", "redeem_reserve"].includes(t)) return "rose";
  if (["opening_balance", "carry_forward"].includes(t)) return "teal";
  return "slate";
}
function statusTone(s) {
  if (s === "active") return "emerald";
  if (s === "pending" || s === "reserved") return "amber";
  if (s === "expired" || s === "reversed" || s === "rejected") return "rose";
  return "slate";
}
